// ** Component Imports
import { Button, ButtonPublic, Dialog, FormWrapper } from '@/shared/ui'

// ** Hook Imports
import { useSubscription } from '../../model/useSubscription'

// ** Styled Components
import {
	StyledDialogContent,
	StyledHeading,
	ButtonsWrapper,
	StyledTypography,
	StyledTitle,
	StyledTypographyPublic
} from './styles'

// ** View Imports
import { Form } from './Form'
import { useNavigate } from 'react-router-dom'

interface IProps {
	label?: string
	variant?: 'primary' | 'secondary' | 'text'
	isOpen?: boolean
	skip?: string
	setOpenCreate?: any
	onOpenChange?: (open: boolean) => void
	dialogTrigger?: React.ReactNode
	onNewPublicPage?: boolean
}

export const EnterpriseModal = ({
	label = 'Contact Sales',
	variant = 'secondary',
	isOpen: propOpen,
	dialogTrigger,
	onNewPublicPage = false,
	skip,
	setOpenCreate,
	onOpenChange
}: IProps) => {
	// ** Hooks
	const {
		isOpen: internalOpen,
		setIsOpen: setInternalOpen,
		errors,
		control,
		onSubmit,
		handleSubmit,
		reset,
		trigger,
		isLoading,
		isSuccess
	} = useSubscription({ initialOpen: propOpen ?? false })

	const navigate = useNavigate()

	// determine source of truth for open state
	const open = propOpen ?? internalOpen

	const handleOpenChange = (open: boolean) => {
		reset()

		if (onOpenChange) {
			onOpenChange(open)
		} else {
			setInternalOpen(open)
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<Dialog.Trigger>
				{dialogTrigger ??
					(propOpen === undefined && (
						<Button label={label} variant={variant} />
					))}
			</Dialog.Trigger>

			<StyledDialogContent
				overlayStyles={{ zIndex: 1001 }}
				contentPositionStyles={{ zIndex: 1001 }}
			>
				{onNewPublicPage ? (
					<StyledTitle>
						{isSuccess ? 'Message sent' : 'Let’s Talk Enterprise'}
					</StyledTitle>
				) : (
					<StyledHeading variant='h2' weight='semi-bold'>
						{isSuccess ? 'Message sent' : 'Let’s Talk Enterprise'}
					</StyledHeading>
				)}

				{onNewPublicPage ? (
					<StyledTypographyPublic>
						{isSuccess
							? 'Thanks for reaching out. Our team will get back to you within 24 hours to customize a plan for your needs.'
							: 'Tell us a bit about your business and we’ll tailor a plan just for you.'}
					</StyledTypographyPublic>
				) : (
					<StyledTypography>
						{isSuccess
							? 'Thanks for reaching out. Our team will get back to you within 24 hours to customize a plan for your needs.'
							: 'Tell us a bit about your business and we’ll tailor a plan just for you.'}
					</StyledTypography>
				)}

				<Dialog.Close styles={{ top: '0.5rem', right: '0.5rem' }} />

				{isSuccess ? (
					<ButtonsWrapper>
						{onNewPublicPage ? (
							<ButtonPublic
								label='Continue'
								variant='primary'
								onClick={() => {
									handleOpenChange(false)
								}}
							/>
						) : (
							<Button
								label='Continue'
								variant='primary'
								onClick={() => {
									handleOpenChange(false)
								}}
							/>
						)}
					</ButtonsWrapper>
				) : (
					<FormWrapper
						onSubmit={handleSubmit((formData) => {
							onSubmit(formData)
						})}
					>
						<Form
							errors={errors}
							control={control}
							trigger={trigger}
							onNewPublicPage={onNewPublicPage}
						/>

						<ButtonsWrapper>
							{onNewPublicPage ? (
								<ButtonPublic
									type='submit'
									label='Send code'
									variant='primary'
									isLoading={isLoading}
									disabled={isLoading}
								/>
							) : (
								<Button
									type='submit'
									label='Send code'
									variant='primary'
									isLoading={isLoading}
									disabled={isLoading}
								/>
							)}

							{!onNewPublicPage &&
								(skip ? (
									<Dialog.Close>
										<Button
											type='button'
											label='Not Now'
											variant='secondary'
											onClick={() => {
												setOpenCreate(false)
												navigate(skip)
											}}
										/>
									</Dialog.Close>
								) : (
									<Dialog.Close>
										<Button type='button' variant='secondary' label='Back' />
									</Dialog.Close>
								))}
						</ButtonsWrapper>
					</FormWrapper>
				)}
			</StyledDialogContent>
		</Dialog>
	)
}
